import React, { useEffect, useRef, useState } from 'react';
import {
  BOARD_LANGUAGES,
  type AdminBoardWorkDto,
  type BookSearchResponseDto,
  type LibraryBookDto,
} from '@living-library/contracts';
import type { BoardRequest } from './board-api';
import { boardError, json } from './board-api';
import { button, c, card, input } from './board-ui';

export function WorkPicker({
  workId,
  onChange,
  onResolved,
  request,
  disabled = false,
  label = c.chooseWork,
}: {
  workId: string;
  onChange(work: AdminBoardWorkDto): void;
  onResolved?(work: AdminBoardWorkDto): void;
  request: BoardRequest;
  disabled?: boolean;
  label?: string;
}) {
  const [work, setWork] = useState<AdminBoardWorkDto | null>(null),
    [query, setQuery] = useState(''),
    [language, setLanguage] = useState<'sk' | 'cs' | 'en'>('sk');
  const [books, setBooks] = useState<LibraryBookDto[]>([]),
    [open, setOpen] = useState(!workId),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [searched, setSearched] = useState(false);
  const callback = useRef(onResolved);
  callback.current = onResolved;
  useEffect(() => {
    let current = true;
    setWork(null);
    if (workId)
      void request<AdminBoardWorkDto>(`/works/${encodeURIComponent(workId)}`)
        .then((value) => {
          if (current) {
            setWork(value);
            callback.current?.(value);
          }
        })
        .catch((error) => {
          if (current) setError(boardError(error));
        });
    return () => {
      current = false;
    };
  }, [request, workId]);
  async function search() {
    setBusy(true);
    setError('');
    try {
      const response = await request<BookSearchResponseDto>(
        `/catalogue?query=${encodeURIComponent(query)}&language=${language}`,
      );
      setBooks(response.items);
      setSearched(true);
    } catch (error) {
      setError(boardError(error));
    } finally {
      setBusy(false);
    }
  }
  async function choose(book: LibraryBookDto) {
    setBusy(true);
    setError('');
    try {
      const selected = await request<AdminBoardWorkDto>(
        '/works/resolve',
        json(
          'POST',
          book.isTransient
            ? { isbn: book.isbn13 ?? book.isbn10, source: book.source, sourceId: book.sourceId }
            : { bookId: book.id },
        ),
      );
      setWork(selected);
      onChange(selected);
      callback.current?.(selected);
      setOpen(false);
      setBooks([]);
    } catch (error) {
      setError(boardError(error));
    } finally {
      setBusy(false);
    }
  }
  const display = work?.editions[language];
  return (
    <div className="min-w-0">
      <span className="text-sm font-semibold">{label}</span>
      {work && (
        <div className="my-3 flex items-start gap-4 rounded-xl bg-panel-raised p-4">
          {display?.coverImageUrl && (
            <img className="h-20 w-14 rounded object-contain" src={display.coverImageUrl} alt="" />
          )}
          <div className="min-w-0">
            <strong className="block">
              {display?.title ?? `${c.editionMissing} ${language.toUpperCase()}`}
            </strong>
            <span className="text-sm text-ink-muted">{display?.authors.join(', ')}</span>
            {work.missingLanguages.length > 0 && (
              <p className="mt-2 text-sm text-warning">
                {c.editionMissing}{' '}
                {work.missingLanguages.map((value) => value.toUpperCase()).join(', ')}
              </p>
            )}
          </div>
        </div>
      )}
      {!disabled && (
        <button className={`${button} my-2`} onClick={() => setOpen(!open)} type="button">
          {workId ? c.change : c.chooseWork}
        </button>
      )}
      {open && !disabled && (
        <div className={`${card} my-2`}>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void search();
            }}
            className="flex flex-wrap items-end gap-2"
          >
            <label className="min-w-36 flex-1 text-sm">
              {c.query}
              <input
                className={input}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                maxLength={200}
              />
            </label>
            <select
              className={`${input} !w-24`}
              aria-label={c.preview}
              value={language}
              onChange={(event) => setLanguage(event.target.value as typeof language)}
            >
              {BOARD_LANGUAGES.map((value) => (
                <option key={value} value={value}>
                  {value.toUpperCase()}
                </option>
              ))}
            </select>
            <button className={button} disabled={busy || !query.trim()}>
              {busy ? c.loading : c.search}
            </button>
          </form>
          <ul className="mt-3 grid max-h-80 gap-2 overflow-y-auto">
            {books.map((book) => (
              <li key={book.id} className="flex items-center gap-3 border-t border-line/30 py-3">
                {book.coverImageUrl && (
                  <img className="h-16 w-11 object-contain" src={book.coverImageUrl} alt="" />
                )}
                <span className="min-w-0 flex-1">
                  <strong className="block break-words">{book.title}</strong>
                  <span className="text-sm text-ink-muted">
                    {book.authors.join(', ')} · {book.language?.toUpperCase()}
                  </span>
                  {book.isTransient && !book.isbn10 && !book.isbn13 && (
                    <span className="mt-1 block text-sm text-warning">{c.isbnRequired}</span>
                  )}
                </span>
                <button
                  className={button}
                  disabled={busy || (book.isTransient && !book.isbn10 && !book.isbn13)}
                  onClick={() => void choose(book)}
                >
                  {c.choose}
                </button>
              </li>
            ))}
          </ul>
          {searched && !books.length && !busy && (
            <p className="mt-3 text-ink-muted">{c.noResults}</p>
          )}
        </div>
      )}
      {error && (
        <p role="alert" className="text-warning">
          {error}
        </p>
      )}
    </div>
  );
}
